import { useMutation } from '@tanstack/react-query';
import type {
  IseeSessionQuery,
  IseeSessionTree,
  IseeStudent,
} from '@tronche2tek/shared-types';
import { useTranslation } from 'react-i18next';

import { getIseeStudents } from '../../api/isee';
import { ErrorMessage } from '../common/ErrorMessage';
import type { IseeQueryState } from './cards-url-state';
import type { IseeTreeFields, Narrower, TreeLevel } from './isee-filters';
import {
  initialTreeFields,
  narrowerOptions,
  NARROWERS,
  pickLevel,
  toggleNarrower,
  toQueryState,
  toSessionQuery,
  TREE_LEVELS,
  treeOptions,
} from './isee-filters';

interface IseeStudentPickerProps {
  /** The sessions running today → +2 months. */
  tree: IseeSessionTree;
  /**
   * The selection, owned by the parent form (restored from the URL). Shown
   * cut down to what the tree still offers — the parent submits it cleaned
   * the same way, so nothing has to be handed back up on mount.
   */
  query: IseeQueryState;
  /** Fires with the full selection whenever a level changes. */
  onQueryChange: (query: IseeQueryState) => void;
}

/**
 * Walks the ISEE sessions tree — school, program, cohort, then a campus or all
 * of them — plus an optional preview of who the cohort holds. The preview is
 * never required to generate: a picked cohort is enough. Its button is a plain
 * button, not a submit: this picker renders inside the card generator's form.
 */
export function IseeStudentPicker({
  tree,
  query: selection,
  onQueryChange,
}: IseeStudentPickerProps) {
  const { t } = useTranslation();
  const fields = initialTreeFields(tree, selection);
  const query = toSessionQuery(toQueryState(fields));
  const options = treeOptions(tree, fields);
  const narrowers = narrowerOptions(tree, fields);

  // react-query owns the preview: its data is the loaded list, its error the
  // only message this panel shows, and `reset()` is how a pick drops both.
  const preview = useMutation({
    mutationFn: (q: IseeSessionQuery) => getIseeStudents(q),
  });
  const students = preview.data?.students ?? null;

  const update = (next: IseeTreeFields) => {
    preview.reset(); // a no-op when nothing has been loaded
    onQueryChange(toQueryState(next));
  };
  const onPicked = (level: TreeLevel, value: string) =>
    update(pickLevel(fields, level, value));
  /** A value to toggle, or the whole list: null selects all, [] none. */
  const onToggled = (narrower: Narrower, value: string | string[] | null) =>
    update({
      ...fields,
      [narrower]:
        typeof value === 'string'
          ? toggleNarrower(fields[narrower], narrowers[narrower], value)
          : value,
    });

  const errorMessage = preview.error
    ? preview.error instanceof Error
      ? preview.error.message
      : String(preview.error)
    : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {TREE_LEVELS.map((level) =>
          // A level opens once the one above it is picked.
          options[level].length === 0 ? null : (
            <LevelSelect
              key={level}
              level={level}
              fields={fields}
              options={options[level]}
              placeholder={t(
                level === 'campus'
                  ? 'cardGenerator.isee.allCampuses'
                  : 'cardGenerator.isee.choose',
              )}
              onPicked={onPicked}
            />
          ),
        )}
        {/* Only a choice when the cohort holds several values — some cards
            are for apprentices only, others for the enrolled. All by default. */}
        {NARROWERS.map((narrower) =>
          narrowers[narrower].length > 1 ? (
            <MultiSelect
              key={narrower}
              narrower={narrower}
              selected={fields[narrower]}
              options={narrowers[narrower]}
              onToggled={onToggled}
            />
          ) : null,
        )}
      </div>

      <button
        type="button"
        onClick={() => query && preview.mutate(query)}
        disabled={!query || preview.isPending}
        className="btn-ghost w-full justify-center disabled:cursor-not-allowed disabled:opacity-40"
      >
        {preview.isPending
          ? t('cardGenerator.isee.loading')
          : t('cardGenerator.isee.loadPreview')}
      </button>

      {errorMessage && <ErrorMessage message={errorMessage} />}

      {students !== null && <StudentPreview students={students} />}
    </div>
  );
}

/** One level of the tree. */
function LevelSelect({
  level,
  fields,
  options,
  placeholder,
  onPicked,
}: {
  level: TreeLevel;
  fields: IseeTreeFields;
  options: string[];
  placeholder: string;
  onPicked: (level: TreeLevel, value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label
        htmlFor={`isee-filter-${level}`}
        className="mb-1 block text-xs font-medium text-ink"
      >
        {t(`cardGenerator.isee.${level}`)}
      </label>
      <select
        id={`isee-filter-${level}`}
        value={fields[level]}
        onChange={(e) => onPicked(level, e.target.value)}
        className="w-full px-3 py-2 text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * A multi-select narrowing the picked cohort, shaped like the selects around
 * it: a button summing up the choice, opening a list of toggles. The list is
 * a native popover — light dismiss and Escape come from the browser — placed
 * under its button by CSS anchor positioning. `selected` null = all of them.
 */
function MultiSelect({
  narrower,
  selected,
  options,
  onToggled,
}: {
  narrower: Narrower;
  selected: string[] | null;
  options: string[];
  onToggled: (narrower: Narrower, value: string | string[] | null) => void;
}) {
  const { t } = useTranslation();
  const ticked = selected && new Set(selected);
  const id = `isee-filter-${narrower}`;
  const anchor = `--${id}`;
  const summary =
    selected === null
      ? t('cardGenerator.isee.all')
      : selected.length === 1
        ? selected[0]
        : t('cardGenerator.isee.someOf', {
            count: selected.length,
            total: options.length,
          });
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-ink">
        {t(`cardGenerator.isee.${narrower}`)}
      </label>
      <button
        id={id}
        type="button"
        popoverTarget={`${id}-menu`}
        style={{ anchorName: anchor }}
        className="flex w-full items-center justify-between border border-rule bg-card px-3 py-2 text-left text-sm text-ink"
      >
        <span className="truncate">{summary}</span>
        <span aria-hidden="true">▾</span>
      </button>
      <div
        id={`${id}-menu`}
        popover="auto"
        style={{ positionAnchor: anchor }}
        className="inset-auto m-0 mt-1 min-w-[anchor-size(width)] border border-rule bg-card p-1 [position-area:bottom_span-right]"
      >
        <div className="mb-1 flex gap-3 border-b border-rule px-2 pb-1 text-xs">
          <button
            type="button"
            onClick={() => onToggled(narrower, null)}
            className="text-blue hover:underline"
          >
            {t('cardGenerator.isee.selectAll')}
          </button>
          <button
            type="button"
            onClick={() => onToggled(narrower, [])}
            className="text-blue hover:underline"
          >
            {t('cardGenerator.isee.selectNone')}
          </button>
        </div>
        {options.map((o) => {
          const on = ticked === null || ticked.has(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              onClick={() => onToggled(narrower, o)}
              className="flex w-full items-center gap-2 px-2 py-1 text-left text-sm text-ink hover:bg-rule"
            >
              <span aria-hidden="true" className="w-3 text-blue">
                {on ? '✓' : ''}
              </span>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Who the picked cohort holds — informative only. */
function StudentPreview({ students }: { students: IseeStudent[] }) {
  const { t } = useTranslation();
  if (students.length === 0) {
    return <p className="text-sm text-muted">{t('cardGenerator.isee.none')}</p>;
  }
  return (
    <div className="max-h-80 overflow-auto border border-rule">
      <table className="w-full text-xs">
        {/* Sticky: the count and the column titles stay in view while the
            list scrolls under them. */}
        <thead className="sticky top-0 bg-card">
          <tr>
            <th
              colSpan={5}
              className="px-2 pt-2 pb-1 text-left text-sm font-medium text-blue"
            >
              {t('cardGenerator.isee.matched', { count: students.length })}
            </th>
          </tr>
          <tr className="border-b border-rule text-left text-muted">
            <th className="px-2 py-1 font-medium">
              {t('cardGenerator.isee.name')}
            </th>
            <th className="px-2 py-1 font-medium">
              {t('cardGenerator.isee.login')}
            </th>
            <th className="px-2 py-1 font-medium">
              {t('cardGenerator.isee.section')}
            </th>
            <th className="px-2 py-1 font-medium">
              {t('cardGenerator.isee.admStatus')}
            </th>
            <th className="px-2 py-1 font-medium">
              {t('cardGenerator.isee.speciality')}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            // Not deduplicated: a student in two sessions is listed twice,
            // once per session — so the session completes the key.
            <tr key={`${s.login}|${s.campus}|${s.section}`}>
              <td className="px-2 py-1 text-ink">
                {`${s.firstname} ${s.lastname}`.trim() || '—'}
              </td>
              <td className="px-2 py-1 text-muted">{s.login}</td>
              <td className="px-2 py-1 whitespace-nowrap text-muted">
                {s.section}
              </td>
              <td className="px-2 py-1 whitespace-nowrap text-blue">
                {s.admStatus || '—'}
              </td>
              <td className="px-2 py-1 whitespace-nowrap text-muted">
                {s.speciality || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
